/**
 * Cart Context
 * Manages shopping cart state across the application
 */

import { createContext, useContext, useState, useMemo } from 'react';
import { getItemType } from '@/constants/itemTypes';
import { 
  generateCartItemKey, 
  calculateItemTotalPrice,
} from '@/utils/pricingHelpers';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  const handleAddToCart = (item, categoryName = null, setAbsoluteQuantity = false) => {
    setCart(prevCart => {
      const itemType = getItemType(item);
      const newItemKey = generateCartItemKey(item);

      // Find existing item using the generated key
      const existingItemIndex = prevCart.findIndex(cartItem => 
        generateCartItemKey(cartItem) === newItemKey
      );

      if (existingItemIndex >= 0) {
        const updatedCart = [...prevCart];
        const existingItem = updatedCart[existingItemIndex];

        const newQuantity = setAbsoluteQuantity
          ? item.quantity
          : existingItem.quantity + (item.quantity || 1);

        // Remove item if quantity is 0 or less
        if (newQuantity <= 0) {
          return updatedCart.filter((_, index) => index !== existingItemIndex);
        }

        // Recalculate total price using unified function
        const totalPrice = calculateItemTotalPrice(existingItem, newQuantity);

        updatedCart[existingItemIndex] = {
          ...existingItem,
          quantity: newQuantity,
          totalPrice: totalPrice
        };
        return updatedCart;
      } else {
        // Skip zero-quantity calls — used by size-group path to clear stale entries that
        // don't exist yet (e.g. unselected sizes on first add).
        const quantity = item.quantity ?? 1;
        if (quantity <= 0) return prevCart;

        // New item - add to cart. Fallback to item.categoryName so callers
        // that don't pass it explicitly (e.g. FreeItemPicker, where the
        // category is resolved at picker time) still tag the line.
        const newItem = {
          ...item,
          quantity,
          categoryName: categoryName ?? item.categoryName ?? null,
          itemType: itemType
        };
        
        // Ensure totalPrice is set
        if (!newItem.totalPrice) {
          newItem.totalPrice = calculateItemTotalPrice(newItem);
        }
        
        return [...prevCart, newItem];
      }
    });
  };

  const handleUpdateCartQuantity = (cartKey, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveFromCart(cartKey);
      return;
    }

    setCart(prevCart =>
      prevCart.map(item => {
        if (generateCartItemKey(item) === cartKey) {
          const totalPrice = calculateItemTotalPrice(item, newQuantity);
          return { ...item, quantity: newQuantity, totalPrice };
        }
        return item;
      })
    );
  };

  const handleRemoveFromCart = (cartKey) => {
    setCart(prevCart => prevCart.filter(item => generateCartItemKey(item) !== cartKey));
  };

  const clearCart = () => {
    setCart([]);
  };

  // Memoized cart totals for performance
  const cartTotals = useMemo(() => {
    const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
    const totalPrice = cart.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    return { totalItems, totalPrice };
  }, [cart]);

  const value = {
    cart,
    handleAddToCart,
    handleUpdateCartQuantity,
    handleRemoveFromCart,
    clearCart,
    cartTotals
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
